import {
  AmpScript,
  GlobalEmailClass,
} from "@redotech/redo-email-content/email-wrapper";
import { EmailFormat } from "@redotech/redo-marketing-templates-common/email-builder";
import { EmailBuilderContext } from "@redotech/redo-web/email-builder-shared/email-builder-context";
import {
  forwardRef,
  memo,
  useContext,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * Outer component responsible for rendering children to a string (rawHtml)
 * and passing it to the inner iframe component.
 */
export const EmailBlockIframeIsolator = memo(
  forwardRef(function EmailBlockIframeIsolator(
    {
      rawHtml,
      scriptUrls,
      format,
      includeWrapperPadding = false,
      interactable = false,
      renderFadeSeconds,
      scale = 1,
    }: {
      rawHtml: string;
      scriptUrls: AmpScript[];
      format: EmailFormat;
      includeWrapperPadding?: boolean;
      interactable?: boolean;
      /**
       * Some complex components have an ugly rerender flash, so we can fade them in for a specific duration if desired
       */
      renderFadeSeconds?: number;
      scale?: number;
    },
    ref,
  ) {
    const divRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Forward the iframe ref to the parent component
    useImperativeHandle(ref, () => iframeRef.current);

    return (
      <div
        ref={divRef}
        style={{
          lineHeight: 0, // not sure why this is needed, but otherwise parent becomes 6px too tall
          pointerEvents: interactable ? undefined : "none",
          width: "100%",
          fontSize: 0, // Prevent any spacing issues that might occur from whitespace
        }}
      >
        <EmailBlockIframeIsolatorHelper
          format={format}
          forwardedRef={iframeRef}
          includeWrapperPadding={includeWrapperPadding}
          interactable={interactable}
          rawHtml={rawHtml}
          renderFadeSeconds={renderFadeSeconds}
          scale={scale}
          scriptUrls={scriptUrls}
        />
      </div>
    );
  }),
);

/**
 * Child component responsible for rendering the iframe.
 * Only rerenders when rawHtml string changes.
 * @param includeWrapperPadding - whether to include root-level email padding in the iframe's height. (the padding that appears on desktop-size emails, wrapping it in a gray bg)
 */
const EmailBlockIframeIsolatorHelper = memo(function AmpIframeIsolator({
  format,
  rawHtml,
  scriptUrls,
  includeWrapperPadding,
  interactable,
  renderFadeSeconds,
  scale = 1,
  forwardedRef,
}: {
  format: EmailFormat;
  rawHtml: string;
  scriptUrls: AmpScript[];
  includeWrapperPadding?: boolean;
  interactable: boolean;
  renderFadeSeconds?: number;
  scale?: number;
  forwardedRef?: React.RefObject<HTMLIFrameElement>;
}) {
  // Use the forwarded ref if provided, otherwise create a local one
  const localIframeRef = useRef<HTMLIFrameElement>(null);
  const iframeRef = forwardedRef || localIframeRef;

  const containerRef = useRef<HTMLDivElement>(null);
  const builderContext = useContext(EmailBuilderContext);
  const screenSize = builderContext?.screenSize;
  const [showOverlay, setShowOverlay] = useState(false);

  // TODO: figure out the root cause of Apple Mail's weird render flickers, and fix them, instead of covering it up here.
  useLayoutEffect(() => {
    if (!renderFadeSeconds) {
      return;
    }
    setShowOverlay(true);
    const timer = setTimeout(() => {
      setShowOverlay(false);
    }, renderFadeSeconds * 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [rawHtml, renderFadeSeconds]);

  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument) {
      return;
    }
    const iframeDocument = iframe.contentDocument;

    // Render the email block in its own isolated iframe
    // Can't do more obvious things like iframeDocument.documentElement.innerHTML = rawHtml
    // Because the <!DOCTYPE html> needs to be set, otherwise browsers render in quirks mode,
    // which messes up the styles.
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Quirks_Mode_and_Standards_Mode
    iframeDocument.open();
    iframeDocument.write(rawHtml);
    iframeDocument.close();

    const { head, body } = iframeDocument;
    body.style.overflow = "hidden"; // weirdly, an empty scrollbar appears occasionally without this
    iframe.width = `${100 / scale}%`;
    iframe.style.transform = `scale(${1 * scale})`;
    iframe.style.transformOrigin = "top left";

    if (format === EmailFormat.AMP) {
      // AMP plays dirty with height, we have to play dirtier
      const style = iframeDocument.createElement("style");
      const moreSpecificPseudoClassesThanAmp = ":first-of-type:not([amp4ads])";
      style.textContent = `html${moreSpecificPseudoClassesThanAmp},body${moreSpecificPseudoClassesThanAmp}{
      height: fit-content !important;
      margin: 0;
      overflow: hidden;
      ${!interactable && "pointer-events: none !important"};
      }`;
      head.append(style);
    }

    // clicking on the iframe doesn't bubble up to the parent automatically
    const postClickMessage = (event: MouseEvent) => {
      containerRef.current?.click();
      event.stopPropagation();
    };

    // We want the merchant to be able to interact with the iframe if interactable is true
    // but naturally, iframes do not bubble up click events to their parent, requiring dirty hacks
    iframeDocument.addEventListener("click", postClickMessage);

    let rafId: number | null = null;
    let resizeObserver: ResizeObserver;

    const adjustHeight = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        if (
          !iframe ||
          !iframeDocument.body ||
          !iframeDocument.body.isConnected
        ) {
          return;
        }
        const mainElement = includeWrapperPadding
          ? iframeDocument.body
          : iframeDocument.body.querySelector(`.${GlobalEmailClass.MJML_BODY}`);
        const newHeight = mainElement?.getBoundingClientRect().height || 0;
        iframe.style.height = `${newHeight}px`; // Use style.height instead of height attribute to preserve fractional pixels
        if (containerRef.current) {
          containerRef.current.style.height = `${newHeight * scale}px`;
        }
      });
    };

    if (iframeDocument.body) {
      resizeObserver = new ResizeObserver(() => {
        adjustHeight();
      });
      resizeObserver.observe(iframeDocument.body);

      return () => {
        resizeObserver.disconnect();
        iframeDocument.removeEventListener("click", postClickMessage);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    }
    return; // appease linter
  }, [
    rawHtml,
    scriptUrls,
    format,
    screenSize,
    includeWrapperPadding,
    interactable,
    scale,
    iframeRef,
  ]);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <iframe ref={iframeRef} sandbox="allow-same-origin" />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          backgroundColor: "var(--redo-colors-background-bg-primary)",
          opacity: showOverlay && renderFadeSeconds ? 1 : 0,
          transition:
            showOverlay && renderFadeSeconds ? undefined : "opacity .2s linear", // pop in, fade out
        }}
      />
    </div>
  );
}, ampIframePropsAreEqual);

/**
 * without this, clicking on the iframe will cause AMP to crash and become blank
 * (this is the reason for the Helper component)
 */
function ampIframePropsAreEqual(prevProps: any, nextProps: any) {
  return (
    prevProps.rawHtml === nextProps.rawHtml &&
    prevProps.scriptUrls === nextProps.scriptUrls &&
    prevProps.forwardedRef === nextProps.forwardedRef
  );
}
